<template>
  <div class="orangehrm-card-container">
    <oxd-text tag="h6" class="orangehrm-main-title">
      {{ request ? 'Revise Remote Work Request' : 'Apply for Remote Work' }}
    </oxd-text>
    <oxd-text v-if="request" class="remote-work-help">
      This will create a new signed application linked to request #{{
        request.id
      }}.
      <template v-if="request.status === 'approved'">
        Your existing approved arrangement remains in place until the revised
        request receives final approval.
      </template>
      The revised arrangement needs supervisor and Executive Director approval
      before it can begin.
    </oxd-text>
    <oxd-divider />
    <oxd-form :loading="isSaving" @submit-valid="onSubmit">
      <oxd-text class="orangehrm-sub-title">Proposed schedule</oxd-text>
      <oxd-text class="remote-work-help">
        Choose the dates and recurring workdays for this arrangement, for up to
        one year. For a single day, use the same start and end date and select
        its weekday. Submit your application before remote work begins.
      </oxd-text>
      <oxd-form-row>
        <oxd-grid :cols="3" class="orangehrm-full-width-grid">
          <oxd-grid-item>
            <date-input
              v-model="form.fromDate"
              label="Start date"
              :rules="rules.fromDate"
              required
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <date-input
              v-model="form.toDate"
              label="End date"
              :rules="rules.toDate"
              required
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-field
              v-model="form.weekdays"
              type="multiselect"
              label="Remote workdays"
              :options="weekdays"
              :rules="rules.weekdays"
              required
            />
          </oxd-grid-item>
        </oxd-grid>
      </oxd-form-row>
      <oxd-form-row>
        <oxd-grid :cols="3" class="orangehrm-full-width-grid">
          <oxd-grid-item>
            <time-input
              v-model="form.startTime"
              label="Start time"
              :rules="rules.startTime"
              required
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <time-input
              v-model="form.endTime"
              label="End time"
              :rules="rules.endTime"
              required
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-field
              v-model="form.timezone"
              type="autocomplete"
              label="Time zone for these hours"
              placeholder="Search by city or region"
              :clear="false"
              :create-options="searchTimezones"
              :rules="rules.timezone"
              required
            />
          </oxd-grid-item>
        </oxd-grid>
      </oxd-form-row>
      <oxd-form-row>
        <oxd-input-field
          v-model="form.scheduleNotes"
          type="textarea"
          label="Schedule details (optional)"
          placeholder="Include breaks, different hours on particular days, and overlap with normal office hours."
          :rules="rules.scheduleNotes"
        />
      </oxd-form-row>
      <oxd-divider />
      <oxd-text class="orangehrm-sub-title">Remote work location</oxd-text>
      <oxd-text class="remote-work-help">
        Provide the full address where you will work. A change of location needs
        approval before you move to the new worksite.
      </oxd-text>
      <oxd-form-row>
        <oxd-grid :cols="2" class="orangehrm-full-width-grid">
          <oxd-grid-item>
            <oxd-input-field
              v-model="form.address.line1"
              label="Street address"
              :rules="rules.addressLine"
              required
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-field
              v-model="form.address.line2"
              label="Apartment, suite, or other address details"
              :rules="rules.addressLine2"
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-field
              v-model="form.address.city"
              label="City"
              :rules="rules.addressPart"
              required
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-field
              v-model="form.address.state"
              label="State / province / region"
              :rules="rules.addressPart"
              required
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-field
              v-model="form.address.postalCode"
              label="Postal code"
              :rules="rules.postalCode"
              required
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-field
              v-model="form.address.country"
              label="Country"
              :rules="rules.addressPart"
              required
            />
          </oxd-grid-item>
        </oxd-grid>
      </oxd-form-row>
      <oxd-divider />
      <oxd-text class="orangehrm-sub-title">Remote work agreement</oxd-text>
      <div class="remote-work-agreement">
        <oxd-text>
          By signing, I confirm that I have read the handbook's Remote Work
          Policy and agree to its conditions, including the following:
        </oxd-text>
        <ul>
          <li>
            I will work from the approved location during the approved hours,
            attend assigned meetings, and maintain reasonable overlap with
            normal office hours. Full-time staff must fulfill their 40-hour
            weekly work requirement; part-time hours are agreed with their
            supervisor.
          </li>
          <li>
            I will remain reachable through University communication platforms,
            normally respond within four hours, and not exceed 24 hours unless
            on an excused absence. I will promptly tell my manager if illness,
            equipment failure, or another issue prevents me from working.
          </li>
          <li>
            I will provide secure, reliable, high-speed internet, maintain
            dependent-care arrangements, and avoid personal business during work
            hours.
          </li>
          <li>
            I will protect confidential information, prevent access by
            non-employees, and save University work and records in its record
            systems. University policies continue to apply at the remote
            worksite.
          </li>
          <li>
            I understand that travel between my home, remote worksite, and the
            office is not reimbursed. Changes to my worksite or hours require
            prior approval. Management may change or withdraw this arrangement
            at any time. This agreement is not an employment contract and does
            not change my at-will employment status.
          </li>
        </ul>
      </div>
      <oxd-form-row>
        <oxd-input-field
          v-model="form.acknowledged"
          type="checkbox"
          label="I have read and agree to the Remote Work Policy and the conditions above."
          :rules="rules.acknowledged"
          required
        />
      </oxd-form-row>
      <oxd-form-row>
        <oxd-grid :cols="2" class="orangehrm-full-width-grid">
          <oxd-grid-item>
            <oxd-input-field
              v-model="form.signature"
              label="Employee signature — type your full name"
              :rules="rules.signature"
              required
            />
          </oxd-grid-item>
        </oxd-grid>
      </oxd-form-row>
      <oxd-text class="remote-work-help">
        Your signed application and submission date will be retained with the
        approval history. Wait for final approval before starting the requested
        arrangement.
      </oxd-text>
      <oxd-text v-if="error" class="remote-work-error" role="alert">
        {{ error }}
      </oxd-text>
      <oxd-divider />
      <oxd-form-actions>
        <required-text />
        <oxd-button
          type="button"
          display-type="ghost"
          label="Cancel"
          :disabled="isSaving"
          @click="$emit('cancel')"
        />
        <submit-button
          :label="request ? 'Submit Revised Request' : 'Submit Request'"
          :disabled="isSaving || !canSubmit"
        />
      </oxd-form-actions>
    </oxd-form>
  </div>
</template>

<script>
import {APIService} from '@/core/util/services/api.service';
import useDateFormat from '@/core/util/composable/useDateFormat';
import {
  required,
  validSelection,
  validDateFormat,
  validTimeFormat,
  shouldNotExceedCharLength,
  endDateShouldBeAfterStartDate,
  endTimeShouldBeAfterStartTime,
} from '@/core/util/validation/rules';
import {
  REMOTE_WORK_PATH,
  remoteWorkError,
  remoteWorkForm,
  remoteWorkPayload,
  remoteWorkWeekdays,
  timezoneOption,
  todayInTimezone,
} from '@/orangehrmLeavePlugin/util/remote-work';

export default {
  name: 'RemoteWorkApply',
  props: {
    request: {type: Object, default: null},
    policyVersion: {type: String, required: true},
    canSubmit: {type: Boolean, default: true},
  },
  emits: ['submitted', 'cancel'],
  setup() {
    const http = new APIService(window.appGlobal.baseUrl, REMOTE_WORK_PATH);
    http.setIgnorePath(REMOTE_WORK_PATH);
    const {userDateFormat} = useDateFormat();
    return {http, userDateFormat};
  },
  data() {
    return {
      isSaving: false,
      error: '',
      form: remoteWorkForm(this.request),
      weekdays: remoteWorkWeekdays,
      rules: {
        fromDate: [
          required,
          validDateFormat(this.userDateFormat),
          (value) =>
            !value ||
            value >= todayInTimezone(this.form.timezone?.id) ||
            'Choose today or a future date in the selected time zone.',
        ],
        toDate: [
          required,
          validDateFormat(this.userDateFormat),
          endDateShouldBeAfterStartDate(
            () => this.form.fromDate,
            'End date must be on or after the start date.',
            {allowSameDate: true},
          ),
        ],
        weekdays: [required],
        startTime: [required, validTimeFormat],
        endTime: [
          required,
          validTimeFormat,
          endTimeShouldBeAfterStartTime(
            () => this.form.startTime,
            'End time must be after the start time on the same day.',
          ),
        ],
        timezone: [required, validSelection],
        addressLine: [required, shouldNotExceedCharLength(255)],
        addressLine2: [shouldNotExceedCharLength(255)],
        addressPart: [required, shouldNotExceedCharLength(100)],
        postalCode: [required, shouldNotExceedCharLength(30)],
        scheduleNotes: [shouldNotExceedCharLength(2000)],
        signature: [required, shouldNotExceedCharLength(150)],
        acknowledged: [
          (value) => value === true || 'Please read and accept the agreement.',
        ],
      },
    };
  },
  watch: {
    'form.fromDate'(date) {
      if (date && !this.form.toDate) this.form.toDate = date;
    },
  },
  methods: {
    async searchTimezones(search = '') {
      const fallback = [
        'America/New_York',
        'America/Chicago',
        'America/Denver',
        'America/Los_Angeles',
        'America/Anchorage',
        'Pacific/Honolulu',
        'Europe/London',
        'Europe/Paris',
        'Asia/Kolkata',
        'Asia/Manila',
        'Asia/Tokyo',
        'Australia/Sydney',
        'UTC',
      ];
      const zones = Intl.supportedValuesOf
        ? Intl.supportedValuesOf('timeZone')
        : fallback;
      const query = search.trim();
      try {
        if (query) {
          const zone = new Intl.DateTimeFormat('en-US', {
            timeZone: query,
          }).resolvedOptions().timeZone;
          if (!zones.includes(zone)) zones.unshift(zone);
        }
      } catch {
        // A partial city name is a search, not yet a selected time zone.
      }
      return zones
        .filter((zone) =>
          zone.replace(/_/g, ' ').toLowerCase().includes(query.toLowerCase()),
        )
        .slice(0, 50)
        .map(timezoneOption);
    },
    async onSubmit() {
      if (this.isSaving || !this.canSubmit) return;
      this.isSaving = true;
      this.error = '';
      try {
        const payload = remoteWorkPayload(this.form, this.policyVersion);
        const response = this.request
          ? await this.http.update(this.request.id, {
              action: 'revise',
              ...payload,
            })
          : await this.http.create(payload);
        this.$toast.success({
          title: 'Request submitted',
          message:
            'Your remote work request has been sent for supervisor review.',
        });
        this.$emit('submitted', response.data.data);
      } catch (error) {
        this.error = remoteWorkError(
          error,
          'Your request could not be submitted. Please check the details and try again.',
        );
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.remote-work-help {
  margin: 0.75rem 0 1rem;
  font-size: 0.875rem;
}
.remote-work-agreement {
  margin: 1rem 0;
  padding: 1rem 1.25rem;
  border-radius: 0.75rem;
  background: $oxd-background-pastel-white-color;
  font-family: $oxd-font-family;
  color: $oxd-interface-gray-darken-1-color;
  font-size: 0.875rem;
  line-height: 1.6;
  ul {
    padding-left: 1.25rem;
    margin-bottom: 0;
  }
  li + li {
    margin-top: 0.6rem;
  }
}
.remote-work-error {
  padding: 1rem;
  margin-top: 1rem;
  border-left: 3px solid $oxd-feedback-danger-color;
}
</style>
